import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { cn } from "@/lib/utils";
import tr from "@/i18n/tr";

export default function DataTable({
    columns,             // [{ key, header, sortable, render(row), width, align }]
    rows,
    loading,
    total,               // for pagination (from server)
    page,
    pageSize,
    onPageChange,
    onPageSizeChange,
    sort,
    order,               // 'asc' | 'desc'
    onSortChange,        // (key, order) -> void
    selectable = false,
    selectedIds = [],
    onSelectionChange,
    getId = (row) => row.id,
    empty = tr.common.noResults,
}) {
    const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));

    const allSelected = useMemo(() => {
        if (!selectable || rows.length === 0) return false;
        return rows.every((r) => selectedIds.includes(getId(r)));
    }, [rows, selectedIds, selectable, getId]);

    const toggleAll = () => {
        if (!selectable) return;
        if (allSelected) {
            const ids = new Set(selectedIds);
            rows.forEach((r) => ids.delete(getId(r)));
            onSelectionChange(Array.from(ids));
        } else {
            const ids = new Set(selectedIds);
            rows.forEach((r) => ids.add(getId(r)));
            onSelectionChange(Array.from(ids));
        }
    };

    const toggleOne = (id) => {
        if (!selectable) return;
        const ids = new Set(selectedIds);
        ids.has(id) ? ids.delete(id) : ids.add(id);
        onSelectionChange(Array.from(ids));
    };

    const handleSort = (col) => {
        if (!col.sortable) return;
        if (sort === col.key) {
            onSortChange(col.key, order === "asc" ? "desc" : "asc");
        } else {
            onSortChange(col.key, "asc");
        }
    };

    return (
        <div className="card-elevated overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full min-w-[900px] border-collapse">
                    <thead>
                        <tr className="border-b border-border bg-muted/40">
                            {selectable && (
                                <th className="px-4 py-3 text-left w-10">
                                    <input
                                        type="checkbox"
                                        checked={allSelected}
                                        onChange={toggleAll}
                                        className="h-4 w-4 rounded border-border accent-[hsl(var(--brand))]"
                                        data-testid="table-select-all"
                                    />
                                </th>
                            )}
                            {columns.map((col) => (
                                <th
                                    key={col.key}
                                    onClick={() => handleSort(col)}
                                    className={cn(
                                        "px-4 py-3 text-left text-[11.5px] font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap",
                                        col.sortable && "cursor-pointer select-none hover:text-foreground",
                                        col.align === "right" && "text-right",
                                        col.align === "center" && "text-center",
                                    )}
                                    style={col.width ? { width: col.width } : undefined}
                                >
                                    <span className="inline-flex items-center gap-1">
                                        {col.header}
                                        {col.sortable && (
                                            sort === col.key ? (
                                                order === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                                            ) : <ArrowUpDown className="h-3 w-3 opacity-40" />
                                        )}
                                    </span>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={columns.length + (selectable ? 1 : 0)} className="px-4 py-16 text-center text-muted-foreground">
                                    {tr.common.loading}
                                </td>
                            </tr>
                        ) : rows.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length + (selectable ? 1 : 0)} className="px-4 py-16 text-center text-muted-foreground">
                                    {empty}
                                </td>
                            </tr>
                        ) : rows.map((row, idx) => {
                            const id = getId(row);
                            const selected = selectable && selectedIds.includes(id);
                            return (
                                <tr
                                    key={id || idx}
                                    className={cn(
                                        "border-b border-border last:border-b-0 hover:bg-muted/40 transition-colors",
                                        selected && "bg-[hsl(var(--brand))]/5",
                                    )}
                                >
                                    {selectable && (
                                        <td className="px-4 py-3">
                                            <input
                                                type="checkbox"
                                                checked={selected}
                                                onChange={() => toggleOne(id)}
                                                className="h-4 w-4 rounded border-border accent-[hsl(var(--brand))]"
                                                data-testid={`table-select-${id}`}
                                            />
                                        </td>
                                    )}
                                    {columns.map((col) => (
                                        <td
                                            key={col.key}
                                            className={cn(
                                                "px-4 py-3 text-[13.5px] text-foreground",
                                                col.align === "right" && "text-right",
                                                col.align === "center" && "text-center",
                                            )}
                                        >
                                            {col.render ? col.render(row) : row[col.key] ?? "—"}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 border-t border-border px-4 py-3 bg-muted/20">
                <div className="text-[12px] text-muted-foreground">
                    {(total || 0) > 0 ? (
                        <>
                            {tr.common.showing}{" "}
                            <span className="font-semibold text-foreground">
                                {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)}
                            </span>{" "}
                            / <span className="font-semibold text-foreground">{total}</span> {tr.common.results}
                        </>
                    ) : (
                        <>0 {tr.common.results}</>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 text-[12px]">
                        <span className="text-muted-foreground">{tr.common.rowsPerPage}</span>
                        <select
                            value={pageSize}
                            onChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
                            className="h-8 rounded-lg border border-border bg-background px-2 text-[12px] focus:outline-none focus:ring-2 focus:ring-brand/40"
                            data-testid="table-page-size"
                        >
                            {[10, 20, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                    </div>
                    <div className="flex items-center gap-1">
                        <button
                            type="button"
                            onClick={() => onPageChange(Math.max(1, page - 1))}
                            disabled={page <= 1}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-background text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                            aria-label="Önceki sayfa"
                            data-testid="table-prev"
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </button>
                        <span className="text-[12.5px] font-medium text-foreground px-2">
                            {page} / {totalPages}
                        </span>
                        <button
                            type="button"
                            onClick={() => onPageChange(Math.min(totalPages, page + 1))}
                            disabled={page >= totalPages}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-background text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                            aria-label="Sonraki sayfa"
                            data-testid="table-next"
                        >
                            <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
